import * as ko from "knockout";
import * as _ from "lodash";
import * as Constants from "../common/constants";
import * as EventUtil from "../common/eventUtil";
import * as Global from "../common/global";
import { TemplatePhraseImportControl } from "../common/templatePhraseImport";
import * as UI from "../common/ui";
import * as templatePhraseData from "../data/templatePhraseData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";

interface IViewModel {
    TemplatePhrase: ITemplatePhraseViewModel;
    IsDoingImport: KnockoutObservable<boolean>;
    Errors: KnockoutObservable<boolean>;
    ExportToExcelUrl: string;
    inactivatePhrase: (phrase: ITemplatePhraseDetailObservable) => void;
    activatePhrase: (phrase: ITemplatePhraseDetailObservable) => void;
    addPhraseDetail: () => void;
    deletePhrase: (detail: ITemplatePhraseDetailObservable) => void;
    undoDeletePhrase: (detail: ITemplatePhraseDetailObservable) => void;
    startImport: () => void;
    startExport: () => void;
}

interface ITemplatePhraseViewModel {
    ID: KnockoutObservable<number>;
    PhraseName: KnockoutObservable<string>;
    IsActive: KnockoutObservable<boolean>;
    AdditionalTextLabel1: KnockoutObservable<string>;
    AdditionalTextLabel2: KnockoutObservable<string>;
    AdditionalNumberLabel1: KnockoutObservable<string>;
    AdditionalNumberLabel2: KnockoutObservable<string>;
    Details: KnockoutObservableArray<ITemplatePhraseDetailObservable>;
    IsPhraseNameMissing: KnockoutObservable<boolean>;
}

// template Phrase Detail for passing data to save and to check if changed
interface ITemplatePhraseDetailObservable {
    ID: KnockoutObservable<number>;
    Phrase: KnockoutObservable<string>;
    SortOrder: KnockoutObservable<number>;
    IsActive: KnockoutObservable<boolean>;
    IsPhraseDetailMissing?: KnockoutObservable<boolean>;
    AdditionalTextValue1: KnockoutObservable<string>;
    AdditionalTextValue2: KnockoutObservable<string>;
    AdditionalNumberValue1: KnockoutObservable<number>;
    AdditionalNumberValue2: KnockoutObservable<number>;
}

export class TemplatePhraseController implements IController {
    private container: JQuery;
    private viewModel: IViewModel;
    private templatePhraseID: number;
    private fieldsOnLoad: string;
    private importControl: TemplatePhraseImportControl;

    constructor() {
        const me: TemplatePhraseController = this;

        this.viewModel = {
            TemplatePhrase: {
                ID: ko.observable(0),
                PhraseName: ko.observable(""),
                IsActive: ko.observable(true),
                AdditionalTextLabel1: ko.observable(""),
                AdditionalTextLabel2: ko.observable(""),
                AdditionalNumberLabel1: ko.observable(""),
                AdditionalNumberLabel2: ko.observable(""),
                Details: ko.observableArray(),
                IsPhraseNameMissing: ko.observable<boolean>(false),
            },
            IsDoingImport: ko.observable(false),
            Errors: ko.observable(false),
            ExportToExcelUrl: `${Global.app().webServiceBaseURL}/api/templatephrase/export`,

            inactivatePhrase(phrase: ITemplatePhraseDetailObservable): void {
                UI.showConfirmDialog("Are you sure you want to make this phrase inactive?",
                    () => { me.togglePhraseStatus(); });
            },
            activatePhrase(phrase: ITemplatePhraseDetailObservable): void {
                UI.showConfirmDialog("Are you sure you want to make this phrase active?",
                    () => { me.togglePhraseStatus(); });
            },
            addPhraseDetail(): void {
                me.addPhraseDetail();
            },
            deletePhrase(detail: ITemplatePhraseDetailObservable): void {
                me.deletePhrase(detail);
            },
            undoDeletePhrase(detail: ITemplatePhraseDetailObservable): void {
                me.undoDeletePhrase(detail);
            },
            startImport(): void {
                me.importControl.load(me.viewModel.TemplatePhrase.ID());
                me.viewModel.IsDoingImport(true);
            },
            startExport(): void {
                me.container.find("form[name='exportTemplatePhrasesForm']").submit();
            },
        };

        this.importControl = new TemplatePhraseImportControl(
            () => {
                me.viewModel.IsDoingImport(false);
            },
            () => {
                me.viewModel.IsDoingImport(false);
                this.loadRecord(this.viewModel.TemplatePhrase.ID());
            },
        );
    }

    public render(container: JQuery): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;

        ko.applyBindings(this.viewModel, container.find("div[data-id='phraseInput']")[0]);
        this.importControl.render(container.find("div[data-id='importControl']"));

        deferred.resolve();
        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        if (params != null && params.length === 1) {
            this.templatePhraseID = params[0];
        } else {
            this.templatePhraseID = 0;
        }

        return this.loadRecord(this.templatePhraseID);
    }

    public save(): JQueryPromise<any> {
        const me: TemplatePhraseController = this;
        const app: IApplication = Global.app();
        let waitContainer: JQuery;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        if (!this.isValid()) {
            UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
            deferred.reject();
            return deferred.promise();
        }

        waitContainer = UI.showWaitIndicator(me.container);
        templatePhraseData.SaveTemplatePhrase(this.getTemplatePhrase())
            .done((response: Data.IResponse<templatePhraseData.ITemplatePhraseResponse>) => {
                if (response.Success) {
                    if (me.viewModel.TemplatePhrase.ID() <= 0) {
                        // me.viewModel.TemplatePhrase.ID(response.Payload.ID);

                        // add a new record, so update the URL
                        me.updateURL(response.Payload.ID, me.viewModel.TemplatePhrase.PhraseName());
                    } else {
                        // updating an existing record
                        app.pageManager().refreshCurrentPageTitle();
                    }
                    me.loadRecord(response.Payload.ID);
                    UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                    deferred.resolve();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .fail(() => {
                deferred.reject();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

        return deferred.promise();
    }

    public haveFieldsChanged(): boolean {
        const fieldValues: string = JSON.stringify(this.getTemplatePhrase());
        if (fieldValues !== this.fieldsOnLoad || this.isImportInProgress()) {
            return true;
        }
        return false;
    }

    public appRouterID(params: any[]): string {
        let routerID: string = "TemplatePhrase";
        if (params != null && params.length === 1) {
            routerID += params[0];
        }
        return routerID;
    }

    // returns a title for the page
    public title(): string {
        return "Template Phrase";
    }

    public subTitle(): string {
        let subTitle: string = "";
        if (this.templatePhraseID == null) {
            subTitle = "New";
        } else {
            subTitle = this.viewModel.TemplatePhrase.PhraseName();
        }
        return subTitle;
    }

    private isImportInProgress(): boolean {
        return this.importControl.isUploadInProcess();
    }

    private loadRecord(templatePhraseID: number): JQueryPromise<any> {
        const me: TemplatePhraseController = this;
        let i: number;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        const templatePhraseRequest: templatePhraseData.ITemplatePhraseRequest = {
            TemplatePhraseID: templatePhraseID,
        };

        templatePhraseData.GetTemplatePhraseMaster(templatePhraseRequest)
            .done((response: Data.IResponse<templatePhraseData.ITemplatePhrase>) => {
                if (response.Success) {

                    me.updateViewModel(response.Payload);
                    // always append 5 blank details
                    for (i = 0; i < 5; i++) {

                        me.addPhraseDetail();
                    }
                    me.fieldsOnLoad = JSON.stringify(me.getTemplatePhrase());

                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }

            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    private updateViewModel(master: templatePhraseData.ITemplatePhrase): void {
        const me: TemplatePhraseController = this;

        me.viewModel.TemplatePhrase.Details([]);
        me.viewModel.TemplatePhrase.ID(master.ID);
        me.viewModel.TemplatePhrase.PhraseName(master.PhraseName);
        me.viewModel.TemplatePhrase.IsActive(master.IsActive);
        me.viewModel.TemplatePhrase.AdditionalTextLabel1(master.AdditionalTextLabel1);
        me.viewModel.TemplatePhrase.AdditionalTextLabel2(master.AdditionalTextLabel2);
        me.viewModel.TemplatePhrase.AdditionalNumberLabel1(master.AdditionalNumberLabel1);
        me.viewModel.TemplatePhrase.AdditionalNumberLabel2(master.AdditionalNumberLabel2);

        _.forEach(master.Details, (detail: templatePhraseData.ITemplatePhraseDetail) => {
            const templatePhraseDetail: ITemplatePhraseDetailObservable = {
                ID: ko.observable(detail.ID),
                Phrase: ko.observable(detail.Phrase),
                SortOrder: ko.observable(detail.SortOrder),
                IsActive: ko.observable(detail.IsActive),
                AdditionalTextValue1: ko.observable(detail.AdditionalTextValue1),
                AdditionalTextValue2: ko.observable(detail.AdditionalTextValue2),
                AdditionalNumberValue1: ko.observable(detail.AdditionalNumberValue1),
                AdditionalNumberValue2: ko.observable(detail.AdditionalNumberValue2),
            };

            me.viewModel.TemplatePhrase.Details.push(templatePhraseDetail);
        });
    }

    private togglePhraseStatus(): JQueryPromise<any> {
        const me: TemplatePhraseController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const newIsActive: boolean = !this.viewModel.TemplatePhrase.IsActive();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(me.container);

        const fieldsChanged: boolean = me.haveFieldsChanged();

        templatePhraseData.ChangeTemplatePhraseStatus(me.viewModel.TemplatePhrase.ID(), newIsActive)
            .done((response: Data.IResponse<any>) => {
                if (response.Success) {
                    me.viewModel.TemplatePhrase.IsActive(newIsActive);

                    if (!fieldsChanged) {
                        me.fieldsOnLoad = JSON.stringify(me.getTemplatePhrase());
                    }

                    UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve(true);
            });

        return deferred.promise();
    }

    private isValid(): boolean {
        let isValid: boolean = true;
        const templatePhrase: any = this.viewModel.TemplatePhrase;

        isValid = UI.setRequiredString(templatePhrase.PhraseName, templatePhrase.IsPhraseNameMissing) ? isValid : false;

        return isValid;
    }

    private addPhraseDetail(): void {
        const me: TemplatePhraseController = this;

        // add new phrase
        const templatePhraseDetail: ITemplatePhraseDetailObservable = {
            ID: ko.observable(0),
            Phrase: ko.observable(""),
            SortOrder: ko.observable(0),
            IsActive: ko.observable(true),
            IsPhraseDetailMissing: ko.observable(false),
            AdditionalTextValue1: ko.observable(""),
            AdditionalTextValue2: ko.observable(""),
            AdditionalNumberValue1: ko.observable(),
            AdditionalNumberValue2: ko.observable(),
        };

        me.viewModel.TemplatePhrase.Details.push(templatePhraseDetail);
    }

    private deletePhrase(detail: ITemplatePhraseDetailObservable): void {
        detail.IsActive(false);
    }

    private undoDeletePhrase(detail: ITemplatePhraseDetailObservable): void {
        detail.IsActive(true);
    }

    // -------------------------------------------------------------------------
    // functions for getting the data structure needed
    // to update the template on the server
    // -------------------------------------------------------------------------

    private getTemplatePhrase(): templatePhraseData.ITemplatePhrase {
        const me: TemplatePhraseController = this;

        const templatePhrase: templatePhraseData.ITemplatePhrase = {
            ID: me.viewModel.TemplatePhrase.ID(),
            PhraseName: me.viewModel.TemplatePhrase.PhraseName(),
            IsActive: me.viewModel.TemplatePhrase.IsActive(),
            AdditionalTextLabel1: me.viewModel.TemplatePhrase.AdditionalTextLabel1(),
            AdditionalTextLabel2: me.viewModel.TemplatePhrase.AdditionalTextLabel2(),
            AdditionalNumberLabel1: me.viewModel.TemplatePhrase.AdditionalNumberLabel1(),
            AdditionalNumberLabel2: me.viewModel.TemplatePhrase.AdditionalNumberLabel2(),
            Details: [],
        };

        _.forEach(me.viewModel.TemplatePhrase.Details(), (detail: ITemplatePhraseDetailObservable) => {
            const templatePhraseDetail: templatePhraseData.ITemplatePhraseDetail = {
                ID: detail.ID(),
                Phrase: detail.Phrase(),
                SortOrder: detail.SortOrder(),
                IsActive: detail.IsActive(),
                AdditionalTextValue1: detail.AdditionalTextValue1(),
                AdditionalTextValue2: detail.AdditionalTextValue2(),
                AdditionalNumberValue1: detail.AdditionalNumberValue1(),
                AdditionalNumberValue2: detail.AdditionalNumberValue2(),

            };

            templatePhrase.Details.push(templatePhraseDetail);
        });

        return templatePhrase;
    }

    // used to update the URL when a new record is added
    private updateURL(newID: number, PhraseName: string): void {
        const app: IApplication = Global.app();
        const routeChange: EventUtil.IRouteChange = {
            appRouterID: this.appRouterID([]),
            url: "/TemplatePhrase/" + newID,
            title: this.title(),
            subTitle: PhraseName,
            newAppRouterID: this.appRouterID([newID]),
        };

        app.events().trigger(EventUtil.EVENT_ROUTECHANGE, routeChange);
    }

}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/templatePhrase.ts